import React, { useEffect, useState } from "react";
import { Loader2, Banknote, CheckCircle2, Clock, XCircle, Wallet, ExternalLink, CalendarDays, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { PageHeader, LoadingBlock, EmptyState } from "../../components/app/ui";
import { payoutApi, codApi, errorMessage, fmtDateTime } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const monthOptions = () => {
  const out = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({ value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: d.toLocaleDateString("en-IN", { month: "long", year: "numeric" }) });
  }
  return out;
};
// Parse a date-only ISO string ("2026-10-21") at local noon so the weekday never drifts across timezones.
const parseDateOnly = (s) => {
  if (!s) return null;
  const [y, m, d] = String(s).split("T")[0].split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, 12, 0, 0);
};
const ddmmyyyy = (dt) => dt
  ? `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}/${dt.getFullYear()}`
  : "";
const STATUS = {
  pending: { label: "Pending", cls: "bg-amber-50 text-amber-700", icon: Clock },
  processing: { label: "Processing", cls: "bg-blue-50 text-blue-700", icon: Clock },
  processed: { label: "Paid", cls: "bg-emerald-50 text-emerald-700", icon: CheckCircle2 },
  collected: { label: "Collected", cls: "bg-emerald-50 text-emerald-700", icon: CheckCircle2 },
  failed: { label: "Failed", cls: "bg-red-50 text-red-700", icon: XCircle },
};

function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.pending;
  const Icon = s.icon;
  return <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${s.cls}`}><Icon className="h-3 w-3" /> {s.label}</span>;
}

export default function Payouts() {
  const { user } = useAuth();
  const isDriver = user?.role === "driver";
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ account_holder: "", account_number: "", ifsc: "", pan: "", upi_id: "" });
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [stmtMonth, setStmtMonth] = useState(monthOptions()[0].value);

  const load = () => payoutApi.me().then((d) => {
    setData(d);
    if (!d.has_account) setEditing(true);
    if (d.account) setForm((f) => ({ ...f, account_holder: d.account.account_holder || "", ifsc: d.account.ifsc || "", pan: d.account.pan || "", upi_id: d.account.upi_id || "" }));
  }).catch(() => setData({ payouts: [], balance: { net: 0 }, has_account: false }));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await payoutApi.saveAccount(form);
      toast.success("Bank details saved");
      setEditing(false);
      setForm((f) => ({ ...f, account_number: "" }));
      load();
    } catch (err) { toast.error(errorMessage(err)); } finally { setSaving(false); }
  };

  if (data === null) return <><PageHeader title="Payouts" /><LoadingBlock rows={3} /></>;
  const bal = data.balance || { net: 0, gross: 0, commission: 0, item_count: 0 };

  return (
    <>
      <PageHeader title="Payouts" description="Your earnings are paid to your bank account every Wednesday, for the Monday–Sunday pay week." />

      {data.next_payout_date && (() => {
        const payoutDate = parseDateOnly(data.next_payout_date);
        const weekday = payoutDate ? payoutDate.toLocaleDateString(undefined, { weekday: "short" }) : "";
        return (
          <div className="mb-6 flex flex-wrap items-center gap-2 rounded-2xl border border-brand-200 bg-brand-50 px-5 py-3 text-sm text-ink" data-testid="payout-next-date">
            <span className="font-semibold">Next payout:</span>
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" className="inline-flex items-center gap-1.5 rounded-full border border-brand-200 bg-white px-3 py-1 font-semibold text-ink hover:bg-brand-50" data-testid="payout-next-date-value">
                  <CalendarDays className="h-4 w-4 text-brand" />
                  {weekday}, {ddmmyyyy(payoutDate)}
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-auto p-0" data-testid="payout-calendar">
                <Calendar mode="single" selected={payoutDate} defaultMonth={payoutDate} disabled={() => true} />
              </PopoverContent>
            </Popover>
            <span className="text-ink-muted">· {data.payout_schedule || "Paid every Wednesday (Mon–Sun week)."}</span>
          </div>
        );
      })()}

      {isDriver && <CodDepositCard />}

      <div className="mb-6 grid gap-4 sm:grid-cols-3" data-testid="payout-balance">
        <div className="kt-card p-5">
          <p className="text-xs uppercase tracking-wider text-ink-muted">Next payout (unsettled)</p>
          <p className={`mt-1 text-2xl font-bold ${bal.net < 0 ? "text-red-600" : "text-ink"}`} data-testid="payout-net">{money(bal.net)}</p>
          {bal.commission > 0 && <p className="mt-1 text-xs text-ink-muted">After commission {money(bal.commission)}{bal.commission_gst > 0 ? ` + ${money(bal.commission_gst)} GST (${bal.commission_gst_pct}%)` : ""}</p>}
          {bal.cash_collected > 0 && <p className="mt-1 text-xs text-ink-muted" data-testid="payout-cash">Earned {money(bal.gross)} · COD cash kept {money(bal.cash_collected)}</p>}
          {bal.net < 0 && <p className="mt-1 text-xs font-medium text-red-600">You are holding our cash — this will be collected from you.</p>}
        </div>
        <div className="kt-card p-5">
          <p className="text-xs uppercase tracking-wider text-ink-muted">Items in this cycle</p>
          <p className="mt-1 text-2xl font-bold text-ink">{bal.item_count || 0}</p>
        </div>
        <div className="kt-card flex items-center gap-3 p-5">
          <Banknote className="h-8 w-8 text-brand" />
          <div>
            <p className="text-xs uppercase tracking-wider text-ink-muted">Payout account</p>
            <p className="mt-0.5 text-sm font-semibold text-ink">{data.account ? `${data.account.account_number_masked} · ${data.account.ifsc}` : "Not added"}</p>
          </div>
        </div>
      </div>

      {!isDriver && (
        <div className="mb-8 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="commission-gst-explainer">
          <p className="text-sm text-amber-900">
            <span className="font-semibold">Why 18% GST on commission?</span> We charge a flat 18% GST on our 13% commission only (not on your whole bill). We collect it and remit it to the government. You're always paid on amounts excluding the customer's 5% GST.
          </p>
          <a href={payoutApi.commissionInvoiceUrl()} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-amber-900 underline" data-testid="commission-invoice-link">
            <Download className="h-3.5 w-3.5" /> Download this month's commission GST invoice
          </a>
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-amber-200 pt-3" data-testid="monthly-statement">
            <span className="text-xs font-semibold text-amber-900">Monthly statement (commission &amp; GST):</span>
            <select value={stmtMonth} onChange={(e) => setStmtMonth(e.target.value)} data-testid="stmt-month-select" className="rounded-lg border border-amber-300 bg-white px-2 py-1.5 text-xs font-medium text-amber-900 focus:border-amber-500 focus:outline-none">
              {monthOptions().map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            <a href={payoutApi.commissionInvoiceUrl(stmtMonth)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full border border-amber-400 px-3 py-1.5 text-xs font-bold text-amber-900 hover:bg-amber-100" data-testid="stmt-download-pdf">
              <Download className="h-3.5 w-3.5" /> Download statement (PDF)
            </a>
          </div>
        </div>
      )}

      <div className="mb-8 kt-card p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-semibold text-ink">Bank account (IMPS/NEFT)</h3>
          {data.has_account && !editing && <Button variant="outline" className="rounded-full" onClick={() => setEditing(true)} data-testid="payout-edit-account">Edit</Button>}
        </div>
        {!data.has_account && <p className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800" data-testid="payout-no-account">Add your bank details to receive your weekly payouts.</p>}
        {editing ? (
          <form onSubmit={save} className="grid gap-4 sm:grid-cols-2" data-testid="payout-account-form">
            <div className="space-y-2"><Label>Account holder name</Label><Input value={form.account_holder} onChange={(e) => setForm((f) => ({ ...f, account_holder: e.target.value }))} data-testid="payout-holder" /></div>
            <div className="space-y-2"><Label>Account number</Label><Input value={form.account_number} onChange={(e) => setForm((f) => ({ ...f, account_number: e.target.value.replace(/\D/g, "") }))} placeholder={data.account ? "Enter again to update" : ""} data-testid="payout-number" /></div>
            <div className="space-y-2"><Label>IFSC code</Label><Input value={form.ifsc} onChange={(e) => setForm((f) => ({ ...f, ifsc: e.target.value.toUpperCase() }))} placeholder="HDFC0001234" data-testid="payout-ifsc" /></div>
            <div className="space-y-2"><Label>PAN <span className="text-ink-muted">(optional)</span></Label><Input value={form.pan} onChange={(e) => setForm((f) => ({ ...f, pan: e.target.value.toUpperCase() }))} data-testid="payout-pan" /></div>
            <div className="space-y-2"><Label>UPI ID <span className="text-ink-muted">(optional)</span></Label><Input value={form.upi_id} onChange={(e) => setForm((f) => ({ ...f, upi_id: e.target.value }))} placeholder="name@bank" data-testid="payout-upi" /></div>
            <div className="sm:col-span-2 flex gap-2">
              <Button type="submit" disabled={saving} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid="payout-save-account">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save bank details</Button>
              {data.has_account && <Button type="button" variant="ghost" className="rounded-full" onClick={() => setEditing(false)}>Cancel</Button>}
            </div>
          </form>
        ) : (
          <p className="text-sm text-ink-soft">{data.account?.account_holder} · {data.account?.account_number_masked} · {data.account?.ifsc}</p>
        )}
      </div>

      <h3 className="mb-3 font-semibold text-ink">Payout history</h3>
      {(!data.payouts || data.payouts.length === 0) ? (
        <EmptyState icon={Banknote} title="No payouts yet" text="Once you have earnings and bank details, your weekly payout will appear here." />
      ) : (
        <div className="grid gap-3" data-testid="payout-history">
          {data.payouts.map((p) => (
            <div key={p.payout_id} className="kt-card flex items-center justify-between p-4" data-testid={`payout-${p.payout_id}`}>
              <div>
                <p className="font-semibold text-ink">{money(p.net)}</p>
                <p className="text-xs text-ink-muted">{new Date(p.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })} · {p.item_count} item{p.item_count === 1 ? "" : "s"}{p.commission > 0 ? ` · fee ${money(p.commission)}` : ""}</p>
                {(p.status === "processed" || p.status === "collected") && (p.payment_mode || p.reference) ? (
                  <p className="mt-0.5 text-xs text-ink-muted" data-testid={`payout-paymeta-${p.payout_id}`}>{p.payment_mode || "manual"}{p.reference ? ` · Ref ${p.reference}` : ""}{p.processed_at ? ` · ${fmtDateTime(p.processed_at)}` : ""}</p>
                ) : null}
              </div>
              <div className="flex flex-col items-end gap-2">
                <StatusBadge status={p.status} />
                <a href={payoutApi.receiptUrl(p.payout_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-brand hover:underline" data-testid={`payout-receipt-${p.payout_id}`}><Download className="h-3.5 w-3.5" /> Receipt</a>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function CodDepositCard() {
  const [sum, setSum] = useState(null);
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null);

  const load = () => codApi.driverSummary().then(setSum).catch(() => setSum(null));
  useEffect(() => { load(); }, []);

  const payManual = async () => {
    if (!ref.trim()) return toast.error("Enter the transfer / UTR reference");
    setBusy(true);
    try { await codApi.payManual(ref.trim(), "upi"); toast.success("Deposit submitted — awaiting verification"); setRef(""); load(); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const payRzp = async () => {
    setBusy(true);
    try {
      const res = await codApi.rzpCreate();
      setLink(res);
      if (res.short_url) window.open(res.short_url, "_blank", "noopener");
      else toast.info("Test mode — tap \"I've paid\" to confirm this deposit.");
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const confirmRzp = async () => {
    if (!link?.deposit?.deposit_id) return;
    setBusy(true);
    try { const r = await codApi.rzpCheck(link.deposit.deposit_id); if (r.paid) { toast.success("Payment confirmed"); setLink(null); load(); } else toast.info("Payment not completed yet"); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };

  if (!sum) return null;
  const due = sum.due || { amount: 0, count: 0, orders: [] };
  const pay = sum.payment || {};

  return (
    <div className="mb-6 kt-card p-5" data-testid="cod-deposit-card">
      <div className="mb-2 flex items-center gap-2"><Wallet className="h-5 w-5 text-brand" /><h3 className="font-semibold text-ink">Food COD to deposit</h3></div>
      <p className="mb-3 text-sm text-ink-soft">{pay.instructions}</p>
      <p className="mb-4 text-2xl font-bold text-ink" data-testid="cod-due-amount">{money(due.amount)} <span className="text-sm font-normal text-ink-muted">from {due.count} order{due.count === 1 ? "" : "s"} (excludes delivery fees)</span></p>

      <div className="mb-4 grid gap-2 rounded-xl border border-surface-200 bg-surface-50 p-4 text-sm sm:grid-cols-2" data-testid="cod-bank-details">
        <div><span className="text-ink-muted">Account holder:</span> <span className="font-medium text-ink">{pay.bank_account_holder}</span></div>
        <div><span className="text-ink-muted">Bank:</span> <span className="font-medium text-ink">{pay.bank_name}</span></div>
        <div><span className="text-ink-muted">A/C:</span> <span className="font-medium text-ink">{pay.account_number}</span></div>
        <div><span className="text-ink-muted">IFSC:</span> <span className="font-medium text-ink">{pay.ifsc}</span></div>
        <div><span className="text-ink-muted">UPI:</span> <span className="font-medium text-ink">{pay.upi_id}</span></div>
        {pay.upi_qr_url ? <div className="sm:col-span-2"><img src={pay.upi_qr_url} alt="UPI QR" className="mt-2 h-32 w-32 rounded-lg border border-surface-200 object-contain" data-testid="cod-upi-qr" /></div> : null}
      </div>

      {due.amount > 0 ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>I paid via bank/UPI — reference (UTR)</Label>
            <div className="flex gap-2">
              <Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="UTR / reference no." data-testid="cod-ref-input" />
              <Button onClick={payManual} disabled={busy} className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid="cod-submit-manual">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit"}</Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Or pay online</Label>
            {!link ? (
              <Button onClick={payRzp} disabled={busy} variant="outline" className="w-full rounded-full" data-testid="cod-pay-razorpay"><ExternalLink className="mr-1 h-4 w-4" /> Pay with Razorpay</Button>
            ) : (
              <Button onClick={confirmRzp} disabled={busy} className="w-full rounded-full bg-emerald-600 text-white hover:bg-emerald-700" data-testid="cod-confirm-razorpay">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "I've paid — confirm"}</Button>
            )}
          </div>
        </div>
      ) : (
        <p className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700" data-testid="cod-all-clear">All caught up — no food COD pending deposit.</p>
      )}

      {(sum.deposits || []).length > 0 && (
        <div className="mt-5">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-muted">Recent deposits</p>
          <div className="grid gap-2" data-testid="cod-deposit-history">
            {sum.deposits.slice(0, 6).map((d) => (
              <div key={d.deposit_id} className="flex items-center justify-between rounded-xl border border-surface-200 px-4 py-2 text-sm" data-testid={`cod-deposit-${d.deposit_id}`}>
                <span className="text-ink">{money(d.amount)} · {d.count} order{d.count === 1 ? "" : "s"} · <span className="capitalize text-ink-muted">{d.method}</span>{d.reference ? ` · ${d.reference}` : ""}</span>
                <span className="flex items-center gap-2">
                  <a href={codApi.receiptUrl(d.deposit_id)} target="_blank" rel="noreferrer" className="text-xs text-brand hover:underline" data-testid={`cod-receipt-${d.deposit_id}`}>Receipt</a>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${d.status === "submitted" ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>{d.status}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
